<script setup lang="ts">
    import EchoButton from '../components/EchoButton.vue';
    import SettingsPageLayout from '../components/settings/SettingsPageLayout.vue';
    import RoleDetails from '../components/roles/RoleDetails.vue';
    import { __ } from '../stores/expressions';
    import { useRouter } from 'vue-router';
    import { ref, type Ref } from 'vue';
    import { useSuccessDialog } from '../stores/success-dialog';
    import { useToastStore } from '../stores/snackbar';
    import { useMutationCreateRole } from '../services/api/role/roleQueries';
    import type { RequestCreateRole } from '../services/api/role/types';
    import axios from 'axios';
    import { CheckIcon, XIcon } from '@lucide/vue';

    const router = useRouter();
    const successDialogStore = useSuccessDialog();
    const toastStore = useToastStore();
    const { mutateAsync: createRoleMutation, isPending: createRoleIsPending } =
        useMutationCreateRole();

    type RoleDetailsRef = {
        submit: () => void;
        canSubmit: boolean;
    };

    const roleDetailsRef: Ref<RoleDetailsRef | null> = ref(null);

    const saveRole = async (
        formData: RequestCreateRole & {
            rolePermissions: Record<string, number>;
        }
    ) => {
        const { rolePermissions: _rolePermissions, ...roleData } = formData;

        let createdRole;

        try {
            createdRole = await createRoleMutation(roleData);
        } catch (error) {
            if (axios.isAxiosError(error) && error.response?.status === 409) {
                toastStore.addToast('error', __('error-role-name-already-exists'));
            } else {
                toastStore.addToast('error', __('error-create-role'));
            }
            return;
        }

        successDialogStore.openDialog({
            message: __('save-role-success-message', {
                name: formData.name,
            }),
        });

        router.push(`/settings/manage-roles/${createdRole.id}/edit`);
    };

    const submitForm = () => {
        roleDetailsRef.value?.submit();
    };

    const cancel = () => {
        router.push('/settings/manage-roles');
    };
</script>
<template>
    <SettingsPageLayout>
        <template #header>
            <h1 class="heading-lg-light">{{ __('manage-roles-add-new-role') }}</h1>
            <p class="text-body">
                {{ __('add-new-role-subtitle') }}
            </p>
        </template>
        <template #topbar>
            <EchoButton
                variant="primary"
                :disabled="!roleDetailsRef?.canSubmit"
                :loading="createRoleIsPending"
                @click="submitForm"
            >
                {{ __('save') }}
                <template #icon-right><CheckIcon /></template>
            </EchoButton>
            <EchoButton variant="secondary" @click="cancel">
                {{ __('cancel') }}
                <template #icon-right><XIcon /></template>
            </EchoButton>
        </template>
        <template #content>
            <RoleDetails ref="roleDetailsRef" @save="saveRole" />
        </template>
    </SettingsPageLayout>
</template>
